import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {UrlService} from '../url.service';

@Injectable()
export class DashboardService {
    private BASE_URL: string;

    constructor(private http: HttpClient,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/v2/dashboard';
    }

    generateRandomId(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    }

    assessmentDataPoints(clientId, instrumentTypes, instrumentBaseTypes, questionIdentifiers, assessmentStatuses, improvableOnly) {
        let params = new HttpParams();
        if (instrumentTypes) {
            params = params.set('instrumentTypes', JSON.stringify(instrumentTypes));
        }
        if (instrumentBaseTypes) {
            params = params.set('instrumentBaseTypes', JSON.stringify(instrumentBaseTypes));
        }
        if (questionIdentifiers) {
            params = params.set('questionIdentifiers', JSON.stringify(questionIdentifiers));
        }
        if (assessmentStatuses) {
            params = params.set('assessmentStatuses', JSON.stringify(assessmentStatuses));
        }
        if (improvableOnly !== null) {
            params = params.set('improvableOnly', improvableOnly);
        }

        const requestOptions = {
            params: params
        }
        return this.http.get(this.BASE_URL + '/' + clientId + '/assessmentDataPoints', requestOptions);
    }

    assessmentAlgorithmDataPoints(clientId, algorithmNames, assessmentStatuses) {
        let params = new HttpParams();
        params = params.set('algorithmNames', JSON.stringify(algorithmNames));
        params = params.set('assessmentStatuses', JSON.stringify(assessmentStatuses));
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clientId + '/assessmentAlgorithmDataPoints', requestOptions);
    }

    mostRecentSingleScore(clientId, instrumentTypes, instrumentBaseTypes, questionIdentifiers, assessmentStatuses) {
        let params = new HttpParams();
        params = params.set('instrumentTypes', JSON.stringify(instrumentTypes));
        params = params.set('instrumentBaseTypes', JSON.stringify(instrumentBaseTypes));
        params = params.set('questionIdentifiers', JSON.stringify(questionIdentifiers));
        params = params.set('assessmentStatuses', JSON.stringify(assessmentStatuses));
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clientId + '/mostRecentSingleScore', requestOptions);
    }

    getClinicianCaseloadItemCounts(clinicianId, instrumentTypes, instrumentBaseTypes,
                                   questionIdentifiers, assessmentStatuses, categoryTitles) {
        let params = new HttpParams();
        params = params.set('instrumentTypes', instrumentTypes);
        params = params.set('instrumentBaseTypes', instrumentBaseTypes);
        params = params.set('questionIdentifiers', questionIdentifiers);
        params = params.set('assessmentStatuses', assessmentStatuses);
        params = params.set('categoryTitles', categoryTitles);
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/clinicianCaseloadItemCounts', requestOptions);
    }

    getClinicianDashboardPlacements(clinicianId, endDateCutoff) {
        let params = new HttpParams();
        params = params.set('endDateCutoff', endDateCutoff);
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/clinicianDashboardPlacements', requestOptions);
    }

    getPlacementAlgorithmData(clinicianId, orgUnitId, algorithmIds, statisticCodes) {
        let params = new HttpParams();
        if (algorithmIds && algorithmIds.length > 0) {
            params = params.set('algorithmIds', algorithmIds);
        }
        statisticCodes.forEach(code => {
            params = params.append('statisticCodes', statisticCodes);
        })
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementAlgorithmDataPoints/', requestOptions);
    }

    getOrgUnitAlgorithmData(orgUnitId, algorithmIds, statisticCodes) {
        let params = new HttpParams();
        params = params.set('algorithmIds', algorithmIds);
        statisticCodes.forEach((code) => {
            params = params.append('statisticCodes', code);
        });
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + orgUnitId + '/orgUnitAlgorithmDataPoints/', requestOptions);
    }

    getOrgUnitData(orgUnitId, statisticCodes) {
        let params = new HttpParams();
        params = params.append('statisticCodes', JSON.stringify(statisticCodes));
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + orgUnitId + '/orgUnitDataPoints/', requestOptions);
    }

    getOrgUnitQuestionData(orgUnitId, questionCompareLinkIdList, statisticCodes) {

        let questionCompareLinkIds = '';
        questionCompareLinkIdList.forEach((id) => {
            if (questionCompareLinkIds.length !== 0) {
                questionCompareLinkIds += ',';
            }
            questionCompareLinkIds += id;
        });
        let params = new HttpParams();
        params = params.set('questionCompareLinkIds', questionCompareLinkIds);
        params = params.set('statisticCodes', JSON.stringify(statisticCodes));
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + orgUnitId + '/orgUnitQuestionDataPoints/', requestOptions);
    }

    getOrgUnitInstrumentData(orgUnitId, statisticCodes) {
        let params = new HttpParams();
        statisticCodes.forEach((code) => {
            params = params.append('statisticCodes', code);
        });
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + orgUnitId + '/orgUnitInstrumentDataPoints/', requestOptions);
    }

    getPlacementServiceCountsData(clinicianId, orgUnitId, statisticCodes) {
        let params = new HttpParams();
        params = params.set('statisticCodes', JSON.stringify(statisticCodes));
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementServiceCountsDataPoints/', requestOptions);
    }

    getPlacementQuestionData(clinicianId, orgUnitId, questionCompareLinkIdList, statisticCodes) {
        let questionCompareLinkIds = '';
        questionCompareLinkIdList.forEach((id) => {
            if (questionCompareLinkIds.length !== 0) {
                questionCompareLinkIds += ',';
            }
            questionCompareLinkIds += id;
        });
        let params = new HttpParams();
        params = params.set('statisticCodes', JSON.stringify(statisticCodes));
        params = params.set('questionCompareLinkIds', questionCompareLinkIds);
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementQuestionDataPoints/', requestOptions);
    }

    getClosedPlacementQuestionData(clinicianId, orgUnitId, questionCompareLinkIdList, statisticCodes, endDateCutoff) {
        let questionCompareLinkIds = '';
        questionCompareLinkIdList.forEach((id) => {
            if (questionCompareLinkIds.length !== 0) {
                questionCompareLinkIds += ',';
            }
            questionCompareLinkIds += id;
        });
        let params = new HttpParams();
        params = params.append('statisticCodes', JSON.stringify(statisticCodes));
        params = params.set('questionCompareLinkIds', questionCompareLinkIds);
        params = params.set('endDateCutoff', endDateCutoff);
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/closedPlacementQuestionDataPoints/', requestOptions);
    }

    getPlacementQuestionCountsData(clinicianId, orgUnitId, questionCompareLinkIdList, statisticCodes, value) {
        let questionCompareLinkIds = '';
        questionCompareLinkIdList.forEach((id) => {
            if (questionCompareLinkIds.length !== 0) {
                questionCompareLinkIds += ',';
            }
            questionCompareLinkIds += id;
        });
        let params = new HttpParams();
        statisticCodes.forEach((code) => {
            params = params.append('statisticCodes', code);
        });
        params = params.set('questionCompareLinkIds', questionCompareLinkIds);
        params = params.set('value', value);
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementQuestionCountsDataPoints/', requestOptions);
    }

    getClientPlacementUserData(clinicianId, orgUnitId, statisticCodes) {
        let params = new HttpParams();
        statisticCodes.forEach((code) => {
            params = params.append('statisticCodes', code);
        });
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementUserDataPoints/', requestOptions);
    }

    getClientPlacementData(clinicianId, orgUnitId, statisticCodes) {
        let params = new HttpParams();
        params = params.set('statisticCodes', JSON.stringify(statisticCodes));
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/' + clinicianId + '/' + orgUnitId + '/placementDataPoints/', requestOptions);
    }

    getMostRecentReasonForAssessmentStatistics(programTagId, agencyTagId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/mostRecentReasons', requestOptions);
    }

    getOrgUnitOverviewData(programTagId, agencyTagId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnitOverviewData', requestOptions);
    }

    getOrgUnitOverviewDataAggregate(programTagId, agencyTagId, timePeriodCode) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('timePeriodCode', timePeriodCode);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnitOverviewDataAggregate', requestOptions);
    }

    getOrgUnitClientDetails(orgUnitId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + orgUnitId + '/clientDetails', requestOptions);
    }

    getOrgUnitClientsReceivingService(orgUnitId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + orgUnitId + '/clientsReceivingServices', requestOptions);
    }

    getOrgUnitClientsReceivingNoServices(orgUnitId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + orgUnitId + '/clientsReceivingNoServices', requestOptions);
    }

    getOrgUnitClientsReceivingServiceAggregate(programTagId, agencyTagId, timePeriodCode) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('timePeriodCode', timePeriodCode);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/clientsReceivingServices/aggregate', requestOptions);
    }

    getOrgUnitTotalServicesByType(orgUnitId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + orgUnitId + '/totalServicesByType', requestOptions);
    }

    getOrgUnitTotalServicesByTypeAggregate(programTagId, agencyTagId, timePeriodCode) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('timePeriodCode', timePeriodCode);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/totalServicesByType/aggregate', requestOptions);
    }

    getOrgUnitClientServicesDistribution(orgUnitId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + orgUnitId + '/clientServicesDistribution', requestOptions);
    }

    getOrgUnitClientServicesDistributionAggregate(programTagId, agencyTagId, timePeriodCode) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('timePeriodCode', timePeriodCode);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/clientServicesDistribution/aggregate', requestOptions);
    }

    getHighRiskClientData(orgUnitId, highRiskTypeId) {
        let params = new HttpParams();
        params = params.set('highRiskTypeId', highRiskTypeId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit' + orgUnitId + '/highRiskClientList', requestOptions);
    }

    checkClinicianDashboardAvailability() {
        return this.http.get(this.BASE_URL + '/clinician/available');
    }

    checkOrgUnitDashboardAvailability() {
        return this.http.get(this.BASE_URL + '/orgUnit/available');
    }

    getHighRiskScores(highRiskTypeId, programTagId, agencyTagId) {
        let params = new HttpParams();
        params = params.set('highRiskTypeId', highRiskTypeId);
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/highRiskScores', requestOptions);
    }

    getPlacementReasonDetailList(ouId, placementStatus, closedDateFrom, closedDateTo) {
        let params = new HttpParams();
        params = params.set('placementStatus', placementStatus);
        params = params.set('closedDateFrom', closedDateFrom);
        params = params.set('closedDateTo', closedDateTo);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/orgUnit/' + ouId + '/placementReasonDetails', requestOptions);
    }

    getAgencyTagsList() {
        return this.http.get(this.BASE_URL + '/agencyTagsList');
    }

    getProgramTagsList() {
        return this.http.get(this.BASE_URL + '/programTagsList');
    }

    getHighRiskTagsList() {
        return this.http.get(this.BASE_URL + '/highRiskTagsList');
    }

    getClinicianAllocationData(programTagId, agencyTagId) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/clinicianAllocationData', requestOptions);
    }

    getClinicianAllocationExpansionData(programTagId, agencyTagId, userId) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('userId', userId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/clinicianAllocationExpansionData', requestOptions);
    }
    getModelOfCareMetadata(programTagId, agencyTagId) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/modelOfCare/metadata', requestOptions);
    }

    getModelOfCareAggregateData(programTagId, agencyTagId) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/modelOfCare/aggregateData', requestOptions);
    }

    getModelOfCareExpansionData(programTagId, agencyTagId, userId) {
        let params = new HttpParams();
        params = params.set('programTagId', programTagId);
        params = params.set('agencyTagId', agencyTagId);
        params = params.set('userId', userId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/modelOfCare/expansionData', requestOptions);
    }

    getModelOfCareServiceProviderList(clientId, clientPlacementId, fromDate, toDate) {
        let params = new HttpParams();
        params = params.set('clientPlacementId', clientPlacementId);
        params = params.set('fromDate', fromDate);
        params = params.set('toDate', toDate);
        params = params.set('clientId', clientId);
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/modelOfCare/serviceProviders', requestOptions);
    }
}
